import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AUTH_SESSION_CLEARED_EVENT, isAuthenticated } from "../utils/authSession";
import { Layout } from "../components/_app/Layout";

import LoginPage from "../pages/auth/LoginPage";
import OtpVerification from "../pages/auth/OtpVerification";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";

import AddUser from "../pages/users/AddUser";
import UserList from "../pages/users/UserList";

import RoleManagement from "../pages/role/RoleManagement";
import OrganizationManagement from "../pages/organizations/OrganizationManagement";
import FrontEndProcessing from "../pages/FrontEndProcessing";
import ProtectedRoute from "../ProtecedRoutes/ProtectedRoute";
import CallCenterCampaignsPage from "../pages/call-center/CallCenterCampaignsPage";
import CallCenterAgentWorkspacePage from "../pages/call-center/CallCenterAgentWorkspacePage";
import CallCenterTicketBulkUploadPage from "../pages/call-center/CallCenterTicketBulkUploadPage";
import CallCenterTicketUploadHistoryPage from "../pages/call-center/CallCenterTicketUploadHistoryPage";
import CallCenterTicketColumnsPage from "../pages/call-center/CallCenterTicketColumnsPage";
import CallCenterTicketSettingsPage from "../pages/call-center/CallCenterTicketSettingsPage";
import CallCenterTicketsListPage from "../pages/call-center/CallCenterTicketsListPage";
import CallCenterSettingsPage from "../pages/call-center/CallCenterSettingsPage";
import CallCenterSlaSettingsPage from "../pages/call-center/CallCenterSlaSettingsPage";
import CallCenterPaymentProofTemplatePage from "../pages/call-center/CallCenterPaymentProofTemplatePage";
import CallCenterDashboardPage from "../pages/call-center/CallCenterDashboardPage";
import CallCenterAlertsPage from "../pages/call-center/CallCenterAlertsPage";
import CallCenterWeeklyTrackerPage from "../pages/call-center/CallCenterWeeklyTrackerPage";
import CallCenterBrowserPhonePage from "../pages/call-center/CallCenterBrowserPhonePage";
import CallCenterPaymentRemindersPage from "../pages/call-center/CallCenterPaymentRemindersPage";
import CallCenterPaymentConfirmationsPage from "../pages/call-center/CallCenterPaymentConfirmationsPage";
import CallCenterAccountPlansPage from "../pages/call-center/CallCenterAccountPlansPage";
import CallCenterAccountPlanTypesPage from "../pages/call-center/CallCenterAccountPlanTypesPage";
import CallCenterWhatsAppSettingsPage from "../pages/call-center/CallCenterWhatsAppSettingsPage";
import CallCenterPaymentConfirmationSettingsPage from "../pages/call-center/CallCenterPaymentConfirmationSettingsPage";
import CallCenterProfilePage from "../pages/call-center/CallCenterProfilePage";
import { getDefaultRouteFromPermissions } from "../utils/permissions";
import { isCallingEnabled } from "../utils/orgFeatures";
import { BrowserPhoneProvider } from "../modules/telephony/browser-phone/BrowserPhoneProvider";
import BrowserPhoneShell from "../modules/telephony/browser-phone/BrowserPhoneShell";
import PermissionBootstrap from "../components/common/PermissionBootstrap";

const Router = () => {
  const [sessionVersion, setSessionVersion] = useState(0);

  useEffect(() => {
    const handleSessionCleared = () => setSessionVersion((value) => value + 1);
    window.addEventListener(AUTH_SESSION_CLEARED_EVENT, handleSessionCleared);
    return () => window.removeEventListener(AUTH_SESSION_CLEARED_EVENT, handleSessionCleared);
  }, []);

  void sessionVersion;

  if (!isAuthenticated()) {
    return <LoginPage />;
  }

  const defaultRoute = getDefaultRouteFromPermissions();

  return (
    <BrowserPhoneProvider>
      <PermissionBootstrap>
        <BrowserPhoneShell>
          <Routes>
      <Route path="/front1234end" element={<FrontEndProcessing />} />
      <Route path="/" element={<Navigate to={defaultRoute} replace />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/otp-verify" element={<OtpVerification />} />
      <Route
        path="/user-profile"
        element={
          <Layout>
            <CallCenterProfilePage />
          </Layout>
        }
      />

      <Route
        path="/call-center/inbox"
        element={
          <Navigate
            to={isCallingEnabled() ? "/call-center/browser-phone" : "/call-center/dashboard"}
            replace
          />
        }
      />
      <Route
        path="/call-center/campaigns"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_CAMPAIGNS" requiredPermission="canView">
              <CallCenterCampaignsPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/tickets"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_TICKETS" requiredPermission="canView">
              <CallCenterTicketsListPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/notifications"
        element={<Navigate to="/call-center/notifications/reminders" replace />}
      />
      <Route
        path="/call-center/notifications/reminders"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_PAYMENT_REMINDERS" requiredPermission="canView">
              <CallCenterPaymentRemindersPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/notifications/confirmations"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_PAYMENT_CONFIRMATIONS" requiredPermission="canView">
              <CallCenterPaymentConfirmationsPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/agent-workspace"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_AGENT_WORKSPACE" requiredPermission="canView">
              <CallCenterAgentWorkspacePage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/ticket-bulk-upload"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_AGENT_WORKSPACE" requiredPermission="canView">
              <CallCenterTicketBulkUploadPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/ticket-upload-history"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_AGENT_WORKSPACE" requiredPermission="canView">
              <CallCenterTicketUploadHistoryPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/ticket-settings"
        element={
          <Layout>
            <ProtectedRoute
              screenCode={["CC_TICKET_COLUMNS", "CC_SETTINGS"]}
              requiredPermission="canView"
            >
              <CallCenterTicketSettingsPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/ticket-columns"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_TICKET_COLUMNS" requiredPermission="canView">
              <CallCenterTicketColumnsPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/account-plans"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_ACCOUNT_PLAN" requiredPermission="canView">
              <CallCenterAccountPlansPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/account-plan-types"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_ACCOUNT_PLAN" requiredPermission="canView">
              <CallCenterAccountPlanTypesPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/settings"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_SETTINGS" requiredPermission="canView">
              <CallCenterSettingsPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/sla-settings"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_SETTINGS" requiredPermission="canView">
              <CallCenterSlaSettingsPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/payment-proof-template"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_PAYMENT_PROOF_TEMPLATE" requiredPermission="canUpdate">
              <CallCenterPaymentProofTemplatePage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/whatsapp-settings"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_WHATSAPP_SETTINGS" requiredPermission="canUpdate">
              <CallCenterWhatsAppSettingsPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/payment-confirmation-settings"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_PAYMENT_CONFIRMATION_SETTINGS" requiredPermission="canUpdate">
              <CallCenterPaymentConfirmationSettingsPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route path="/call-center" element={<Navigate to="/call-center/dashboard" replace />} />
      <Route path="/call-center/analytics" element={<Navigate to="/call-center/dashboard" replace />} />
      <Route
        path="/call-center/dashboard"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_DASHBOARD" requiredPermission="canView">
              <CallCenterDashboardPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/alerts"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_AGENT_ALERTS" requiredPermission="canView">
              <CallCenterAlertsPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/call-center/weekly-tracker"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_WEEKLY_TRACKER" requiredPermission="canView">
              <CallCenterWeeklyTrackerPage />
            </ProtectedRoute>
          </Layout>
        }
      />

      <Route
        path="/call-center/browser-phone"
        element={
          <Layout>
            <ProtectedRoute screenCode="CC_INBOX" requiredPermission="canView">
              <CallCenterBrowserPhonePage />
            </ProtectedRoute>
          </Layout>
        }
      />

      <Route
        path="/all-users"
        element={
          <Layout>
            <ProtectedRoute screenCode="USER_MANAGEMENT" requiredPermission="canView">
              <UserList />
            </ProtectedRoute>
          </Layout>
        }
      />

      <Route
        path="/add-user"
        element={
          <Layout>
            <ProtectedRoute screenCode="USER_MANAGEMENT" requiredPermission="canCreate">
              <AddUser />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/add-user/:userId"
        element={
          <Layout>
            <ProtectedRoute screenCode="USER_MANAGEMENT" requiredPermission="canUpdate">
              <AddUser />
            </ProtectedRoute>
          </Layout>
        }
      />

      <Route
        path="/organizations"
        element={
          <Layout>
            <ProtectedRoute screenCode="ORG_MANAGEMENT" requiredPermission="canView">
              <OrganizationManagement />
            </ProtectedRoute>
          </Layout>
        }
      />

      <Route
        path="/role-management"
        element={
          <Layout>
            <ProtectedRoute screenCode="ROLE_MANAGEMENT" requiredPermission="canView">
              <RoleManagement />
            </ProtectedRoute>
          </Layout>
        }
      />
          </Routes>
        </BrowserPhoneShell>
      </PermissionBootstrap>
    </BrowserPhoneProvider>
  );
};

export default Router;
